import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from 'antd';
import { DesktopOutlined, InfoOutlined, MobileOutlined, TabletOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { FreeRoundType, localStorageHelper } from 'utils';
import { ITimeZoneListItem } from '../../helpers/interfaces';

enum CampaignStatus {
    PENDING = 'PENDING',
    FAILED = 'FAILED',
    ON_SIMULATION = 'ON_SIMULATION',
    PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
    EXPIRED = 'EXPIRED',
    FINISHED = 'FINISHED',
}
const stringToInteger = (numberString: any) => {
    if (numberString === null) {
        return -1;
    }

    const string = numberString.toString();
    const numberWithoutCommas = string.replace(/,/g, '');
    const number = parseFloat(numberWithoutCommas);

    if (isNaN(number)) {
        return 0;
    }

    return number;
};

function createSorter(property: string) {
    return (a: { [key: string]: any }, b: { [key: string]: any }): number => {
        return stringToInteger(a[property]) - stringToInteger(b[property]);
    };
}

export const PlayerReportColumns = (
    activeCurrencyCode: string,
    getGameHistoryData: Function,
    playerId: any,
    playerRawData: any,
    login: any,
    // roundTypes: string[],
) => {
    const { t } = useTranslation();
    // const includeFree = roundTypes.includes('free-round');
    // const includeBuyFeature = roundTypes.includes('buy');
    // const includeAwardedFeature = roundTypes.includes('awarded-feature');
    // const includeRegular = roundTypes.includes('regular');
    const isPrimaryFlow =
        localStorageHelper.getChainedValue('user.brandFeatures.freeRoundsType') ===
        FreeRoundType.PRIMARY_FLOW;
    const isDirectFreeRounds = localStorageHelper.getChainedValue(
        'user.brandFeatures.directFreeRoundsSupporting',
    );
    const listOfGamesInDataset: any = Array.from(
        new Set(playerRawData?.map((item: any) => item.gameName)),
    ).map((gameName: any) => ({
        text: gameName,
        value: gameName,
    }));
    const timezoneName = localStorageHelper.getChainedValue('user.preferences.timeZone');
    const timezoneData = localStorageHelper
        .getChainedValue('user.timezoneList')
        .find((elem: ITimeZoneListItem) => elem.id === timezoneName);
    const { offset } = timezoneData;

    return [
        {
            title: t('game_name'),
            label: 'game_name',
            dataIndex: 'gameName',
            key: 'gameName',
            sorter: createSorter('gameName'),
            width: 185,
            fixed: true,
            filters: listOfGamesInDataset,
            onFilter: (value: any, record: any) => {
                return record.gameName.indexOf(value as string) === 0;
            },
            render: (test: any, record: any) => (
                <span>{record.gameName + ' | ID: ' + record.gameId}</span>
            ),
        },
        {
            title: t('roundId'),
            label: 'roundId',
            dataIndex: 'roundId',
            key: 'roundId',
            width: 185,
        },
        {
            title: t('game_session_id'),
            label: 'game_session_id',
            dataIndex: 'gameSessionId',
            key: 'game_session_id',
            sorter: createSorter('gameSessionId'),
            width: 165,
        },
        {
            title: t('country_city'),
            label: 'country_city',
            dataIndex: 'country',
            key: 'country',
            sorter: createSorter('country'),
            width: 165,
            render: (text: string, record: any) => (
                <span>{text ? `${text} | ${record.city}` : t('unknown')}</span>
            ),
        },
        {
            title: t('played_at'),
            label: 'played_at',
            dataIndex: 'endTime',
            key: 'played_at',
            render: (text: any) => (
                <>
                    <span>{text}</span>
                    <br />
                    <span style={{ color: '#AAAEB7' }}>UTC{offset}</span>{' '}
                </>
            ),
            sorter: (a: any, b: any) =>
                new Date(a.endTime).getTime() - new Date(b.endTime).getTime(),
            width: 200,
        },
        {
            title: t('device_type'),
            label: 'device_type',
            dataIndex: 'deviceType',
            key: 'device_type',
            render: (text: string) => {
                if (text === 'desktop') return <DesktopOutlined className="device-type" />;
                if (text === 'tablet') return <TabletOutlined className="device-type" />;
                else return <MobileOutlined className="device-type" />;
            },
            width: 90,
        },
        {
            title: 'Paid',
            label: 'type',
            dataIndex: 'type',
            key: 'type',
            width: 165,
        },
        {
            title: 'Round Type',
            label: 'Round Type',
            dataIndex: 'roundType',
            key: 'roundType',
            width: 165,
            render: (value: string) => {
                const map: Record<string, string> = {
                    regular: 'Normal round',
                    'free-round': 'Free round',
                    buy: 'Buy feature',
                    'awarded-feature': 'Awarded feature ',
                };
                return map[value] || value;
            },
            sorter: (a: any, b: any) => {
                const mapOrder: Record<string, number> = {
                    regular: 1,
                    free: 2,
                    buy_feature: 3,
                    awarded_feature: 4,
                };
                return (mapOrder[a.roundType] || 99) - (mapOrder[b.roundType] || 99);
            },
        },
        {
            title: t('Status'),
            label: 'Status',
            dataIndex: 'status',
            key: 'Status',
            width: 165,
            render: (value: string) => <span>{t(value)}</span>,
        },
        {
            title: isPrimaryFlow ? (
                t('promotion')
            ) : (
                <div style={{ display: 'flex' }}>
                    {t('promotion')}{' '}
                    <Tooltip title={t('promotion_highlight_tooltip')}>
                        <InfoOutlined />
                    </Tooltip>
                </div>
            ),
            label: 'promotion',
            dataIndex: 'promotionId',
            key: 'promotion',
            width: 165,
            className: 'campaign-status-column',
            render: (text: string, record: any) => {
                const { currencyId, promotionId, gameSessionId } = record;
                const isFreeRound = !record.bought && record.type === 'Free';
                const isAwardedRound = record.bought && record.type === 'Free';
                const directFreeRoundsUrl = `promotions/${promotionId}`;
                const awardedFeatureUrl = `awarded-promotion/${promotionId}`;
                const reverseFreeRoundsUrl = `promotion/promotion-${playerId}+${promotionId}+${gameSessionId}+${currencyId}+${login}`;
                let queryParams;

                if (isFreeRound) {
                    if (isDirectFreeRounds) {
                        queryParams = directFreeRoundsUrl;
                    } else {
                        queryParams = reverseFreeRoundsUrl;
                    }
                } else if (isAwardedRound) {
                    queryParams = awardedFeatureUrl;
                }

                return text ? (
                    <div
                        className={
                            !isPrimaryFlow && record.status !== CampaignStatus.FINISHED
                                ? 'marked-campaign'
                                : ''
                        }
                    >
                        <Link className="login" to={`/casino/${queryParams}`}>
                            {text}
                        </Link>
                    </div>
                ) : (
                    <span>{t('n_a')}</span>
                );
            },
        },
        {
            title: () => (
                <span className="table-title__span">
                    {t('bet')}
                    <span>{activeCurrencyCode}</span>
                </span>
            ),
            label: 'bet',
            dataIndex: 'bet',
            key: 'bet',
            sorter: createSorter('bet'),
            width: 165,
        },
        {
            title: () => (
                <span className="table-title__span">
                    {t('win')}
                    <span>{activeCurrencyCode}</span>
                </span>
            ),
            label: 'win',
            dataIndex: 'win',
            key: 'win',
            sorter: createSorter('win'),
            width: 165,
        },
        {
            title: () => (
                <span className="table-title__span">
                    {t('net')}
                    <span>{activeCurrencyCode}</span>
                </span>
            ),
            label: 'net',
            dataIndex: 'net',
            key: 'net',
            sorter: createSorter('net'),
            width: 165,
        },
        {
            title: () => (
                <span className="table-title__span">
                    {t('start_balance')}
                    <span>{activeCurrencyCode}</span>
                </span>
            ),
            label: 'start_balance',
            dataIndex: 'startBalance',
            key: 'start_balance',
            sorter: createSorter('startBalance'),
            width: 165,
            render: (text: any, record: any) => {
                if (text === null || text === undefined || text === '') {
                    const calculatedStartBalance = stringToInteger(record.balance) + stringToInteger(record.bet) - stringToInteger(record.win);
                    return new Intl.NumberFormat('en', {

                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    }).format(calculatedStartBalance);
                }
                return text;
            },
        },
        {
            title: () => (
                <span className="table-title__span">
                    {t('end_balance')}
                    <span>{activeCurrencyCode}</span>
                </span>
            ),
            label: 'end_balance',
            dataIndex: 'balance',
            key: 'end_balance',
            sorter: createSorter('balance'),
            width: 165,
        },
        {
            title: t('visual_log'),
            label: 'visual_log',
            dataIndex: 'callback',
            key: 'visual_log',
            render: (_text: string, record: any) => {
                const rawData = playerRawData.find((data: any) => data.roundId === record.roundId);
                const playerData = { ...rawData, playerId };

                const onClick = () => {
                    getGameHistoryData(playerData);
                };
                return <Button onClick={onClick}>Visual log</Button>;
            },
            width: 165,
        },
    ];
};
